import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';

export default function TaxSummaryReport() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [companySnap, setCompanySnap] = useState(null);
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  // Calculate
  let taxCollected6 = 0;
  let taxCollected8 = 0;
  let taxCollectedService = 0;
  let serviceTaxRateFound = 0;

  orders.forEach(order => {
    const sst6Amount = toNumber(order.sst6Amount);
    const sst8Amount = toNumber(order.sst8Amount);
    const serviceTaxAmount = toNumber(order.serviceTax10Amount);
  
    // Sum taxes separately
    if (sst6Amount > 0) {
      taxCollected6 += sst6Amount;
    }
    if (sst8Amount > 0) {
      taxCollected8 += sst8Amount;
    }
    if (serviceTaxAmount > 0) {
      taxCollectedService += serviceTaxAmount;
      if (!serviceTaxRateFound) {
        const r = toNumber(order.serviceTax10Rate);
        if (r > 0) serviceTaxRateFound = r;
      }
    }
  });

  const totalTaxCollected = taxCollected6 + taxCollected8 + taxCollectedService;
  const serviceTaxLabel = serviceTaxRateFound ? `Service Tax (${serviceTaxRateFound}%)` : 'Service Tax';

  const loadOrders = async () => {
    if (!user || !companyId) return;
    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      where('paymentStatus', '!=', 'Void'),
      orderBy('paymentStatus'),
      orderBy('timestamp', 'desc')
    );
    const snap = await getDocs(q);
    const data = snap.docs.map(doc => doc.data()).filter(order => {
      const orderDate = parseTimestamp(order.timestamp);
      if (!orderDate) return false;
      const from = fromDate ? new Date(fromDate) : new Date(0);
      const to = toDate ? new Date(toDate) : new Date();
      to.setHours(23, 59, 59, 999);
      return orderDate >= from && orderDate <= to;
    });
    setOrders(data);
  };

  const loadCompany = async () => {
    if (!user || !companyId) return;
    const snap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
    if (snap.exists()) setCompanySnap(snap.data());
  };

  const exportToPDF = () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
  
    const doc = new jsPDF();
    const title = `Tax Summary Report (${formatDate(fromDate)} - ${formatDate(toDate)})`;

    doc.setFontSize(12);
    doc.text(companySnap.companyName, 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(title, 105, 22, { align: 'center' });

    autoTable(doc, {
      head: [['Category', 'Amount (RM)']],
      body: [
        ['SST (6%)', taxCollected6.toFixed(2)],
        ['SST (8%)', taxCollected8.toFixed(2)],
        [serviceTaxLabel, taxCollectedService.toFixed(2)],
        ['Total Tax Collected', totalTaxCollected.toFixed(2)],
      ],
      startY: 30,
      styles: { fontSize: 9 }
    });

    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });

    const file = `tax-summary_${formatDate(fromDate)}_to_${formatDate(toDate)}.pdf`.replace(/\//g, '-');
    doc.save(file);
  };

    useEffect(() => {
      const loadAll = async () => {
        setLoading(true);
        await Promise.all([loadOrders(), loadCompany()]);
        setLoading(false);
      };
      loadAll();
    }, [user, fromDate, toDate, companyId]);
  
    if (loading) {
      return (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading information...</span>
        </div>
      );
    }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Tax Summary Report</h2>

      <div className="flex gap-4 mb-6 flex-wrap">
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <span className="text-gray-400">to</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-[#1f1f1f] px-3 py-2 rounded" />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="space-y-4 bg-[#1a1a1a] rounded-lg p-6">
        <div className="flex justify-between">
          <span>SST (6%)</span>
          <span>RM {taxCollected6.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>SST (8%)</span>
          <span>RM {taxCollected8.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>{serviceTaxLabel}</span>
          <span>RM {taxCollectedService.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-green-400">
          <span>Total Tax Collected</span>
          <span>RM {totalTaxCollected.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
